import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { integrationApi, type APIKey, type APIKeyWithSecret, type DeveloperPortalSummary } from '../../api/integration';

export function DeveloperPortalPage() {
  const [portal, setPortal] = useState<DeveloperPortalSummary | null>(null);
  const [keys, setKeys] = useState<APIKey[]>([]);
  const [newKey, setNewKey] = useState<APIKeyWithSecret | null>(null);
  const [keyName, setKeyName] = useState('');
  const [error, setError] = useState('');

  const refresh = () => {
    void integrationApi.developerPortal().then(setPortal).catch((e) => setError(String(e)));
    void integrationApi.listAPIKeys().then(setKeys).catch(() => {});
  };

  useEffect(() => { refresh(); }, []);

  const createKey = async () => {
    if (!keyName.trim()) return;
    try {
      const k = await integrationApi.createAPIKey({ name: keyName, scopes: ['integrations:read', 'integrations:write'] });
      setNewKey(k);
      setKeyName('');
      refresh();
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold dark:text-slate-100">Developer Portal</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">API documentation, keys, usage analytics and SDK access.</p>
      <Link to="/integrations/gateway" className="mt-2 inline-block text-sm text-teal-700 hover:underline dark:text-teal-400">← API Gateway</Link>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: 'Catalog Size', value: portal?.catalog_size ?? 0 },
          { label: 'API Keys', value: portal?.api_keys ?? 0 },
          { label: 'Connectors', value: portal?.connectors ?? 0 },
          { label: 'Webhooks', value: portal?.webhook_count ?? 0 },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border p-4 dark:border-slate-700 dark:bg-slate-900">
            <p className="text-sm text-slate-500">{s.label}</p>
            <p className="mt-1 text-2xl font-semibold">{s.value}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 rounded-xl border p-4 dark:border-slate-700">
        <h2 className="font-medium">Create API Key</h2>
        <div className="mt-3 flex gap-2">
          <input
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            placeholder="Key name"
            className="flex-1 rounded border px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"
          />
          <button type="button" onClick={createKey} className="rounded bg-teal-600 px-4 py-2 text-sm text-white hover:bg-teal-700">Create</button>
        </div>
        {newKey && (
          <div className="mt-4 rounded bg-amber-50 p-3 text-sm dark:bg-amber-900/20">
            <p className="font-medium">Save this secret — it won&apos;t be shown again:</p>
            <code className="mt-1 block break-all text-xs">{newKey.secret}</code>
          </div>
        )}
      </div>
      <div className="mt-6">
        <h2 className="font-medium">Active API Keys</h2>
        <ul className="mt-2 space-y-2">
          {keys.map((k) => (
            <li key={k.id} className="rounded border px-3 py-2 text-sm dark:border-slate-700">
              {k.name} · <span className="font-mono text-xs">{k.key_prefix}…</span> · {k.status}
            </li>
          ))}
        </ul>
      </div>
      {portal?.openapi_spec_url && (
        <p className="mt-6 text-sm text-slate-500">OpenAPI spec: {portal.openapi_spec_url}</p>
      )}
    </div>
  );
}
